/**
 * Wrapper around the browser `fetch` that:
 *
 *  1. Prefixes relative paths with `base_url_be` (so requests reach the
 *     Rust API on :8080 in dev instead of hitting the Vite dev server).
 *  2. Injects the PASETO access token from `localStorage` as a Bearer
 *     `Authorization` header — same pattern as the axios interceptor in
 *     `AxiosInstance.ts`, applied to the raw-fetch call sites that the
 *     legacy code reaches for chat streaming and data queries.
 *
 *  Drop-in replacement for `fetch()` — same signature and return type,
 *  including support for streaming `Response` bodies (does NOT consume
 *  the body; callers stream `response.body` themselves).
 */

import { base_url_be } from '../constants/global'

function joinUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const baseTrimmed = base.replace(/\/+$/, '')
  const pathTrimmed = path.startsWith('/') ? path : `/${path}`
  return `${baseTrimmed}${pathTrimmed}`
}

export async function authedFetch(
  input: string | URL | Request,
  init?: RequestInit,
): Promise<Response> {
  const url =
    typeof input === 'string'
      ? joinUrl(base_url_be, input)
      : input instanceof URL
        ? input
        : input

  const headers = new Headers(init?.headers)
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return fetch(url, { ...init, headers })
}
